<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="show" class="no-access-overlay">
        <!-- ── Not Found screen ── -->
        <div v-if="accessStore.notFound" class="not-found-screen">
          <div class="not-found-code">404</div>
          <h2 class="not-found-title">Project not found</h2>
          <p class="not-found-desc">
            This project doesn't exist or the link may be broken.<br />
            Check the URL and try again.
          </p>
          <div class="not-found-actions">
            <button class="btn btn-primary" @click="goToProjects">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Go to My Projects
            </button>
          </div>
          <p v-if="userEmail" class="not-found-signed-in">
            Signed in as <strong>{{ userEmail }}</strong>
          </p>
        </div>

        <!-- ── Access request screen (existing) ── -->
        <div v-else class="no-access-modal">
          <!-- Decorative background -->
          <div class="modal-bg-decoration"></div>

          <!-- Icon -->
          <div class="modal-icon" :class="{ 'icon-success': requestSent }">
            <svg
              v-if="!requestSent"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <svg
              v-else
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>

          <!-- Title -->
          <h2 class="modal-title">
            <template v-if="requestSent">Request Sent!</template>
            <template v-else-if="isNewProject">Almost There!</template>
            <template v-else-if="!isSignedIn">Sign In Required</template>
            <template v-else>Request Access</template>
          </h2>

          <!-- Message -->
          <p class="modal-message">
            <template v-if="requestSent">
              Your request has been sent to the project owner. You'll be
              notified when they respond.
            </template>
            <template v-else-if="isNewProject">
              Sign in to continue with your newly-created project.
            </template>
            <template v-else-if="!isSignedIn">
              Sign in to view this project or request access from the owner.
            </template>
            <template v-else>
              {{
                message ||
                "You don't have permission to view this project. Request access from the owner."
              }}
              <span v-if="userEmail" class="signed-in-as">
                Signed in as <strong>{{ userEmail }}</strong>
              </span>
            </template>
          </p>

          <!-- Actions -->
          <div class="modal-actions">
            <template v-if="requestSent">
              <button class="btn btn-primary" @click="goToProjects">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Go to My Projects
              </button>
            </template>
            <template v-else-if="!isSignedIn">
              <button class="btn btn-primary" @click="goToSignIn">
                Sign In
              </button>
            </template>
            <template v-else>
              <button
                class="btn btn-primary"
                @click="handleRequestAccess"
                :disabled="requesting"
              >
                <svg
                  v-if="!requesting"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                <span v-if="requesting" class="spinner"></span>
                {{ requesting ? 'Sending...' : 'Request Access' }}
              </button>
              <button class="btn btn-secondary" @click="goToProjects">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                My Projects
              </button>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
/* eslint-disable */
import { ref, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/store/userStore';
import { useAccessStore } from '@/store/accessStore';
import { requestJoin } from '@/api/project';
import { hapticModal, haptic } from '@/utils/haptics';

const props = defineProps<{
  show: boolean;
  message?: string;
  isNewProject?: boolean;
  projectId?: string;
}>();

const emit = defineEmits<{
  close: [];
  accessRequested: [];
}>();

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const accessStore = useAccessStore();

const requestSent = ref(false);
const requesting = ref(false);

// Duolingo-style: haptic feedback when modal appears
watch(() => props.show, (val) => {
  if (val) hapticModal();
});

const isSignedIn = computed(() => !!userStore.idToken);
const userEmail = computed(() => userStore.user?.email || '');

function goToSignIn() {
  const currentPath = route.fullPath;
  // Don't redirect back to auth pages (avoid redirect loops)
  const isAuthPath = currentPath.startsWith('/auth') || currentPath.startsWith('/p1/auth');
  if (isAuthPath) {
    router.push('/auth');
  } else {
    router.push({ path: '/auth', query: { redirect: currentPath } });
  }
  emit('close');
}

function goToProjects() {
  router.push('/projects');
  emit('close');
}

async function handleRequestAccess() {
  if (!props.projectId || requesting.value) return;

  requesting.value = true;
  try {
    const success = await requestJoin(props.projectId);
    if (success) {
      haptic('success');
      requestSent.value = true;
      emit('accessRequested');
    }
  } finally {
    requesting.value = false;
  }
}
</script>

<style scoped>
.no-access-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.no-access-modal {
  background: white;
  border-radius: 20px;
  padding: 48px 40px;
  max-width: 460px;
  width: 90%;
  text-align: center;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: #6366f1;
}

.modal-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
}

.modal-icon.icon-success {
  background: #dcfce7;
  color: #16a34a;
}

.modal-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.modal-message {
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 28px 0;
}

.signed-in-as {
  display: block;
  margin-top: 12px;
  font-size: 14px;
  color: #94a3b8;
}

.signed-in-as strong {
  color: #64748b;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 40px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  white-space: nowrap;
  min-width: 180px;
}

.btn-primary {
  background: #6366f1;
  color: white;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: #4f46e5;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  position: relative;
  left: -5px;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Transition */
.modal-fade-enter-active {
  animation: fadeIn 0.25s ease;
}

.modal-fade-leave-active {
  animation: fadeIn 0.2s ease reverse;
}

.modal-fade-enter-active .no-access-modal {
  animation: slideUp 0.3s ease;
}

.modal-fade-leave-active .no-access-modal {
  animation: slideUp 0.2s ease reverse;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 480px) {
  .no-access-modal {
    padding: 24px;
    margin: 16px;
  }
}
</style>
